import React, { useState, useMemo, useCallback } from 'react';
import {
	Box,
	Typography,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Checkbox,
	FormControlLabel,
	Button,
	Chip,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	InputAdornment,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Divider
} from '@mui/material';
import {
	ExpandMore as ExpandMoreIcon,
	Add as AddIcon,
	Public as PublicIcon,
	Search as SearchIcon,
	Check as CheckIcon,
	Close as CloseIcon
} from '@mui/icons-material';
import { ShippingRegion, CompanyShippingRegion } from '../services/shippingRegions';

interface Props {
	allRegions: ShippingRegion[];
	selectedRegions: CompanyShippingRegion[];
	onBulkUpdate: (addCountries: string[], removeCountries: string[]) => void;
	shippingMethodId: number;
	companyId: number;
}

// Continent mapping for better organization
const CONTINENT_MAPPING: Record<string, string> = {
	'NA': 'North America',
	'SA': 'South America',
	'EU': 'Europe',
	'AS': 'Asia',
	'AF': 'Africa',
	'OC': 'Oceania',
	'AN': 'Antarctica'
};

// Group regions by continent based on region field
const groupRegionsByContinent = (regions: ShippingRegion[]) => {
	const grouped: Record<string, ShippingRegion[]> = {};
	
	regions.forEach(region => {
		const continent = CONTINENT_MAPPING[region.region] || 'Other';
		if (!grouped[continent]) {
			grouped[continent] = [];
		}
		grouped[continent].push(region);
	});
	
	// Sort continents and regions within each continent
	return Object.keys(grouped)
		.sort()
		.reduce((acc, continent) => {
			acc[continent] = grouped[continent].sort((a, b) => a.name.localeCompare(b.name));
			return acc;
		}, {} as Record<string, ShippingRegion[]>);
};

export const ShippingMethodRegionsSelector: React.FC<Props> = ({
	allRegions,
	selectedRegions,
	onBulkUpdate,
}) => {
	const [isAddModalOpen, setIsAddModalOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [expandedContinent, setExpandedContinent] = useState<string | null>(null);
	
	// Auto-expand all continents when searching
	const shouldAutoExpand = searchTerm.trim().length > 0;
	
	// Track pending changes in the modal
	const [pendingChanges, setPendingChanges] = useState<{
		toAdd: Set<string>;
		toRemove: Set<string>;
	}>({ toAdd: new Set(), toRemove: new Set() });
	
	const groupedRegions = useMemo(() => groupRegionsByContinent(allRegions), [allRegions]);
	const selectedRegionCodes = useMemo(() => new Set(selectedRegions.map(r => r.countryCode)), [selectedRegions]);
	
	// Filter regions based on search term
	const filteredGroupedRegions = useMemo(() => {
		if (!searchTerm.trim()) return groupedRegions;
		
		const filtered: Record<string, ShippingRegion[]> = {};
		Object.entries(groupedRegions).forEach(([continent, regions]) => {
			const filteredRegions = regions.filter(region =>
				region.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
				continent.toLowerCase().includes(searchTerm.toLowerCase())
			);
			if (filteredRegions.length > 0) {
				filtered[continent] = filteredRegions;
			}
		});
		return filtered;
	}, [groupedRegions, searchTerm]);
	
	// Get effective selection state (original + pending changes)
	const getEffectiveSelection = useCallback((regionCode: string) => {
		const originallySelected = selectedRegionCodes.has(regionCode);
		const pendingAdd = pendingChanges.toAdd.has(regionCode);
		const pendingRemove = pendingChanges.toRemove.has(regionCode);
		
		if (pendingAdd) return true;
		if (pendingRemove) return false;
		return originallySelected;
	}, [selectedRegionCodes, pendingChanges]);
	
	const handleContinentToggle = (continent: string) => {
		setExpandedContinent(expandedContinent === continent ? null : continent);
	};
	
	const handleBulkSelect = (continent: string, checked: boolean) => {
		const continentRegions = filteredGroupedRegions[continent] || [];
		continentRegions.forEach(region => {
			const isCurrentlySelected = getEffectiveSelection(region.code);
			if (checked && !isCurrentlySelected) {
				setPendingChanges(prev => ({
					...prev,
					toAdd: new Set(Array.from(prev.toAdd).concat(region.code)),
					toRemove: new Set(Array.from(prev.toRemove).filter(code => code !== region.code))
				}));
			} else if (!checked && isCurrentlySelected) {
				setPendingChanges(prev => ({
					...prev,
					toRemove: new Set(Array.from(prev.toRemove).concat(region.code)),
					toAdd: new Set(Array.from(prev.toAdd).filter(code => code !== region.code))
				}));
			}
		});
	};
	
	const handleRegionToggle = (regionCode: string, checked: boolean) => {
		if (checked) {
			setPendingChanges(prev => ({
				...prev,
				toAdd: new Set(Array.from(prev.toAdd).concat(regionCode)),
				toRemove: new Set(Array.from(prev.toRemove).filter(code => code !== regionCode))
			}));
		} else {
			setPendingChanges(prev => ({
				...prev,
				toRemove: new Set(Array.from(prev.toRemove).concat(regionCode)),
				toAdd: new Set(Array.from(prev.toAdd).filter(code => code !== regionCode))
			}));
		}
	};
	
	const getContinentSelectionStatus = (continent: string) => {
		const continentRegions = filteredGroupedRegions[continent] || [];
		const selectedCount = continentRegions.filter(r => getEffectiveSelection(r.code)).length;
		
		if (selectedCount === 0) return 'none';
		if (selectedCount === continentRegions.length) return 'all';
		return 'partial';
	};
	
	const handleAddSelectedRegions = () => {
		// Send bulk update with all pending changes
		const addCountries = Array.from(pendingChanges.toAdd);
		const removeCountries = Array.from(pendingChanges.toRemove);
		
		if (addCountries.length > 0 || removeCountries.length > 0) {
			onBulkUpdate(addCountries, removeCountries);
		}
		
		// Reset modal state
		setIsAddModalOpen(false);
		setSearchTerm('');
		setPendingChanges({ toAdd: new Set(), toRemove: new Set() });
	};
	
	const handleModalClose = () => {
		setIsAddModalOpen(false);
		setSearchTerm('');
		setPendingChanges({ toAdd: new Set(), toRemove: new Set() });
	};
	
	const continents = Object.keys(filteredGroupedRegions).sort();
	
	// Calculate total pending changes
	const totalPendingChanges = pendingChanges.toAdd.size + pendingChanges.toRemove.size;
	
	return (
		<Box>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
				<Typography variant="body2" color="text.secondary">
					Available in ({selectedRegions.length})
				</Typography>
				<Button
					variant="outlined"
					startIcon={<AddIcon />}
					onClick={() => setIsAddModalOpen(true)}
					size="small"
				>
					{selectedRegions.length === 0 ? 'Add Regions' : 'Edit'}
				</Button>
			</Box>
			
			{selectedRegions.length > 0 ? (
				<Box display="flex" flexWrap="wrap" gap={0.5}>
					{selectedRegions.slice(0, 10).map(region => (
						<Chip
							key={region.countryCode}
							label={region.name}
							size="small"
							variant="outlined"
							sx={{ 
								fontSize: '0.75rem',
								height: 24,
								'& .MuiChip-label': {
									px: 1
								}
							}}
						/>
					))}
					{selectedRegions.length > 10 && (
						<Chip
							label={`+${selectedRegions.length - 10} more`}
							size="small"
							variant="outlined"
							sx={{ 
								fontSize: '0.75rem',
								height: 24,
								'& .MuiChip-label': {
									px: 1
								}
							}}
						/>
					)}
				</Box>
			) : (
				<Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
					No regions selected
				</Typography>
			)}
			
			{/* Add Regions Modal */}
			<Dialog 
				open={isAddModalOpen} 
				onClose={handleModalClose}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>
					<Box display="flex" justifyContent="space-between" alignItems="center">
						<Typography variant="h6">Configure Shipping Regions</Typography>
						<IconButton onClick={handleModalClose} size="small">
							<CloseIcon />
						</IconButton>
					</Box>
				</DialogTitle>
				
				<Box sx={{px: 2}}>
					<TextField
						fullWidth
						placeholder="Search regions or continents..."
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							),
						}}
						sx={{ mb: 1 }}
					/>
				</Box>
				<DialogContent sx={{pt: 0}}>
					{continents.length > 0 ? (
						<Box>
							{continents.map(continent => {
								const regions = filteredGroupedRegions[continent];
								const selectionStatus = getContinentSelectionStatus(continent);
								const isExpanded = expandedContinent === continent;
								
								return (
									<Accordion
										key={continent}
										expanded={shouldAutoExpand || isExpanded}
										onChange={() => handleContinentToggle(continent)}
										sx={{ mb: 1 }}
									>
										<AccordionSummary
											expandIcon={<ExpandMoreIcon />}
											sx={{
												'&:hover': {
													backgroundColor: 'action.hover'
												}
											}}
										>
											<Box display="flex" alignItems="center" width="100%">
												<PublicIcon sx={{ mr: 1, color: 'primary.main' }} />
												<Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
													{continent}
												</Typography>
												<FormControlLabel
													control={
														<Checkbox
															checked={selectionStatus === 'all'}
															indeterminate={selectionStatus === 'partial'}
															onChange={(e) => handleBulkSelect(continent, e.target.checked)}
															onClick={(e) => e.stopPropagation()}
															size="small"
														/>
													}
													label=""
													sx={{ mr: 0 }}
												/>
											</Box>
										</AccordionSummary>
										
										<AccordionDetails sx={{ p: 0 }}>
											<List dense>
												{regions.map((region, index) => (
													<Box key={region.code}>
														<ListItem disablePadding>
															<ListItemButton
																onClick={() => handleRegionToggle(region.code, !getEffectiveSelection(region.code))}
																sx={{ py: 0.5 }}
															>
																<Checkbox
																	checked={getEffectiveSelection(region.code)}
																	size="small"
																	sx={{ mr: 1 }}
																/>
																<ListItemText 
																	primary={region.name}
																	primaryTypographyProps={{ variant: 'body2' }}
																/>
															</ListItemButton>
														</ListItem>
														{index < regions.length - 1 && <Divider />}
													</Box>
												))}
											</List>
										</AccordionDetails>
									</Accordion>
								);
							})}
						</Box>
					) : (
						<Box textAlign="center" py={3}>
							<Typography variant="body1" color="text.secondary">
								No regions found matching &quot;{searchTerm}&quot;
							</Typography>
						</Box>
					)}
				</DialogContent>
				
				<DialogActions>
					<Button onClick={handleModalClose}>
						Cancel
					</Button>
					<Button 
						onClick={handleAddSelectedRegions}
						variant="contained"
						startIcon={<CheckIcon />}
						disabled={totalPendingChanges === 0}
					>
						{totalPendingChanges > 0 
							? `Update Regions (${totalPendingChanges})`
							: 'No Changes'
						}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}; 